import { HttpErrorResponse } from '@angular/common/http';
import {
	Component,
	ElementRef,
	HostListener,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { debounceTime, fromEvent, map, switchMap, take, tap } from 'rxjs';
import { SubSink } from 'subsink';

// --- PrimeNG ---
import { MenuItem } from 'primeng/api';
import { OverlayPanel } from 'primeng/overlaypanel';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// --- Components ---
import { SearchBoxComponent } from '@components/search-box/search-box.component';
import { CartDialogComponent } from '../dialogs/cart-dialog/cart-dialog.component';
import { FavoritesDialogComponent } from '../dialogs/favorites-dialog/favorites-dialog.component';

// --- Facades ---
import { AppearanceFacade } from '@facades/appearance.facade';
import { AuthFacade } from '@facades/auth.facade';
import { CartFacade } from '@facades/cart.facade';
import { CategoriesFacade } from '@facades/categories.facade';
import { MenuLayoutFacade } from '@facades/menu-layout.facade';
import { ParametersFacade } from '@facades/parameters.facade';
import { StoresFacade } from '@facades/stores.facade';
import { TenantsFacade } from '@facades/tenants.facade';

// --- Services ---
import { SharedEventService } from '@services/shared-event.service';
import { SidebarUiService } from '@services/sidebar-ui.service';
import { UnreadOrderMsgsService } from '../../services/unread-order-msgs.service';

// --- Utils ---
import { getNameLabel } from '@utils/name-label.util';
import { canCreatePortalOrders } from '@utils/portal-orders-permissions.util';

// --- Interfaces ---
import type { ICategory } from '@interfaces/category';
import { EUserProfileIds, type IUser } from '@interfaces/user';

// --- Facades (seller order — read-only usage) ---
import { SellerOrderFacade } from '@facades/seller-order.facade';

interface CustomMenuItem extends MenuItem {
	roles: EUserProfileIds[];
}

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
	private subs = new SubSink();
	ref?: DynamicDialogRef;

	isSmallScreen = false;
	/** Full mobile header chrome (two lines, sidebar toggle, Categorias + search) ≤700. */
	isMobileHeader = false;
	/** Tablet: Categorias button beside search on one line (701–960). */
	isCompactNav = false;
	isSearchRowHidden = false;
	private lastScrollY = 0;

	@ViewChild('planTooltip', { static: false }) planTooltip!: OverlayPanel;

	readonly authState$ = this.authFacade.authState$;

	accountOptions: CustomMenuItem[] = [
		{
			label: 'Minha conta',
			icon: 'fa-solid fa-user',
			routerLink: ['account'],
			roles: [
				EUserProfileIds.CUSTOMER,
				EUserProfileIds.CUSTOMER_ADMIN,
				EUserProfileIds.APPLICATION_ADMIN,
				EUserProfileIds.PORTAL_MANAGER,
				EUserProfileIds.SELLER,
			],
		},
		{
			label: 'Sair',
			icon: 'fa-solid fa-arrow-right-from-bracket',
			command: () => this.logout(),
			roles: [
				EUserProfileIds.CUSTOMER,
				EUserProfileIds.CUSTOMER_ADMIN,
				EUserProfileIds.APPLICATION_ADMIN,
				EUserProfileIds.PORTAL_MANAGER,
				EUserProfileIds.SELLER,
			],
		},
	];

	autocompleteOverlayRef!: OverlayPanel;
	badge: number = 0;
	categories: ICategory[] = [];
	elements = ['#dashboard__menu'];
	hasPackingFeature: boolean = this.tenantsFacade.hasFlag(
		'enableProductPacking'
	);
	isNotPortalManager: boolean = false;
	isLoggingOut = false;
	isSearching = false;
	items!: MenuItem[];
	search: string = '';
	searchInputRef!: SearchBoxComponent;
	settingsButton!: { label: string; icon: string; routerLink: string[] };
	maxCategories = 5;
	subcategories: ICategory[] = [];

	showMessagesTab = false;
	readonly parameters = this.parametersFacade.parameters;
	unreadOrderMessages$ = this.unreadOrderMsgsService.unreadMessages$;

	/** Seller order CTA — portalOrders + createOrders (see canCreatePortalOrders). */
	canCreatePortalOrder = false;
	draftCount = 0;
	sellerOrderMenuItems: MenuItem[] = [];

	constructor(
		private _elementRef: ElementRef,
		public _dialogService: DialogService,
		private _router: Router,
		private appearanceFacade: AppearanceFacade,
		private authFacade: AuthFacade,
		private cartFacade: CartFacade,
		private categoriesFacade: CategoriesFacade,
		private menuLayoutFacade: MenuLayoutFacade,
		private parametersFacade: ParametersFacade,
		private sellerOrderFacade: SellerOrderFacade,
		private sharedEventService: SharedEventService,
		private storeFacade: StoresFacade,
		private tenantsFacade: TenantsFacade,
		private sidebarUi: SidebarUiService,
		public unreadOrderMsgsService: UnreadOrderMsgsService
	) {}

	get isSidebarCollapsed(): boolean {
		return this.sidebarUi.isCollapsed;
	}

	@HostListener('window:resize', [])
	checkScreenSize() {
		const width = window.innerWidth;

		this.isSmallScreen = width <= 768;
		this.isMobileHeader = width <= 700;
		this.isCompactNav = width > 700 && width <= 960;
		if (!this.isMobileHeader) {
			this.isSearchRowHidden = false;
		}
		if (width > 1250) {
			this.maxCategories = 5;
		} else if (width > 960 && width <= 1250) {
			this.maxCategories = 3;
		} else {
			this.maxCategories = 5;
		}
	}

	@HostListener('window:scroll', [])
	onWindowScroll() {
		this.hideTooltip();
		if (!this.isMobileHeader) return;

		const currentY = window.scrollY || document.documentElement.scrollTop || 0;
		const delta = currentY - this.lastScrollY;

		if (currentY <= 8) {
			this.isSearchRowHidden = false;
		} else if (delta > 8) {
			this.isSearchRowHidden = true;
		} else if (delta < -8) {
			this.isSearchRowHidden = false;
		}

		this.lastScrollY = currentY;
	}

	toggleSidebar() {
		this.sidebarUi.toggle();
	}

	ngOnInit() {
		this.checkScreenSize();
		this.lastScrollY = window.scrollY || 0;
		this.initializeSubscriptions();
		this.subs.add(
			fromEvent(window, 'resize')
				.pipe(debounceTime(200))
				.subscribe(() => {
					this.checkScreenSize();
					if (this.categories?.length) this.menu(); // atualiza o menu
				})
		);

		this.showMessagesTab = this.parameters?.enableOrderMessages ? true : false;
	}

	ngOnDestroy() {
		this.subs.unsubscribe();
	}

	private get searchBoxInput() {
		return this.searchInputRef?.searchInputRef.nativeElement;
	}

	private get searchHasFocus() {
		return document.activeElement === this.searchBoxInput;
	}

	get appearance() {
		const { logo, useTheme } = this.appearanceFacade;
		return { logo, useTheme };
	}

	get cartTotalItems() {
		return this.cartFacade.totalItems || 0;
	}

	get currentStore() {
		return this.storeFacade.currentStore;
	}

	get menuLayout() {
		return this.menuLayoutFacade.menuLayout;
	}

	get searchBoxClientRect() {
		return this.searchBoxInput?.getBoundingClientRect() || { width: 0 };
	}

	/** Mobile/compact: full content width; desktop: match search input. */
	get autocompletePanelStyle(): Record<string, string> {
		if (this.isMobileHeader || this.isCompactNav) {
			return { width: 'calc(100vw - 2rem)' };
		}
		return { width: `${this.searchBoxClientRect.width}px` };
	}

	get stockDecimalPlaces() {
		return (this.parametersFacade.parameters?.stockDecimalPlaces || 0) > 0
			? 1
			: 0;
	}

	private initializeSubscriptions() {
		this.subs.add(
			this.authFacade.authState$.pipe(take(1)).subscribe(({ user }) => {
				const isPortalManager =
					user?.profileId === EUserProfileIds.PORTAL_MANAGER;
				if (isPortalManager) return;

				this.isNotPortalManager = true;

				this.canCreatePortalOrder = canCreatePortalOrders(user, {
					portalOrdersEnabled: this.tenantsFacade.hasFeatures('portalOrders'),
				});

				if (this.canCreatePortalOrder) {
					this.buildSellerOrderMenu();
					this.subs.add(
						this.sellerOrderFacade.loadDrafts().subscribe(drafts => {
							this.draftCount = drafts?.length ? 1 : 0;
							this.buildSellerOrderMenu();
						})
					);
					this.subs.add(
						this.sellerOrderFacade.state$.subscribe(state => {
							const count = state.drafts?.length ? 1 : 0;
							if (count === this.draftCount) return;
							this.draftCount = count;
							this.buildSellerOrderMenu();
						})
					);
				}

				this.getCategories();

				this.subs.add(
					this.sharedEventService
						.receiveEvent()
						.pipe(switchMap(() => this.getCategories()))
						.subscribe()
				);
			})
		);
	}

	private showTooltip(event: any) {
		this.planTooltip?.toggle(event);
	}

	private hideTooltip() {
		this.planTooltip?.hide();
	}

	private getCategories() {
		return this.categoriesFacade.getAllCategories().pipe(
			map(res => res.result),
			tap({
				next: res => {
					this.categories = res.filter(category => category.parentId === null);
					this.categories = this.menuLayout?.customizeCategoriesMenu
						? this.getCustomMenu()
						: this.categories;
					this.subcategories = res.filter(category => category.parentId !== 0);

					this.appearanceFacade.showMenu = Boolean(this.categories.length);
					this.items = [];
					this.menu();
				},
				error: err => {
					if (err instanceof HttpErrorResponse) {
						const isUnauthorized = err.status === 401;
						if (isUnauthorized) this.authFacade.logout();
					}
				},
			})
		);
	}

	private getCustomMenu() {
		const categories = this.categories
			.filter(category => category.exhibitionOrder !== null)
			.sort((a, b) => a.exhibitionOrder - b.exhibitionOrder);

		return categories;
	}

	private menu() {
		this.items = [];

		let super_obj = {} as any;

		const number = this.maxCategories;

		const condition = this.categories.length > number;

		let fixedCategories: any = [];

		const objCategory = () => {
			this.categories.forEach((category: any) => {
				let obj = {} as any;

				let obj_fixed = {} as any;
				if (condition && fixedCategories.length < number) {
					obj_fixed.label = category.name;
					(obj_fixed.routerLink = ['/']),
						(obj_fixed.queryParams = { category: category.id }),
						(obj_fixed.target = '_self'),
						(obj_fixed.styleClass = 'menu__subcategories'),
						(obj_fixed.items = []);
				}

				obj.label = category.name;
				(obj.routerLink = ['/']),
					(obj.queryParams = { category: category.id }),
					(obj.styleClass = 'menu__subcategories'),
					(obj.target = '_self'),
					(obj.items = []);

				if (!condition) obj.target = '_self';
				obj.items = [];
				this.subcategories.forEach((subcategory: any) => {
					if (subcategory.parentId === category.id)
						obj.items.push({
							label: subcategory.name,
							routerLink: ['/'],
							styleClass: 'subcategory__menu__all',
							queryParams: { category: subcategory.id },
						});
					if (
						condition &&
						fixedCategories.length < number &&
						subcategory.parentId === category.id
					)
						obj_fixed.items?.push({
							label: subcategory.name,
							routerLink: ['/'],
							styleClass: 'subcategory__menu',
							queryParams: { category: subcategory.id },
						});
				});
				condition ? super_obj.items.push(obj) : this.items.push(obj);
				condition && Object.keys(obj_fixed).length > 0
					? fixedCategories.push(obj_fixed)
					: null;
			});
			condition ? this.items.push(super_obj) : null;
			condition && fixedCategories.length > 0
				? this.items.push(...fixedCategories)
				: null;

			this.items = this.items.map((category: any) => {
				if (category.items.length === 0) delete category.items;
				else {
					category.items.map((insideCategory: any) => {
						if (insideCategory?.items?.length === 0)
							delete insideCategory.items;
					});
				}
				return category;
			});
		};

		if (condition) {
			super_obj.label = 'Todas Categorias';
			super_obj.styleClass = 'menu__all__categories';
			super_obj.items = [];
		}

		objCategory();
	}

	checkHasFeatures() {
		for (const element of this.elements) {
			const nativeElement =
				this._elementRef.nativeElement.querySelector(element);

			nativeElement
				?.closest('li')
				?.addEventListener('mouseenter', this.showTooltip.bind(this));
		}
	}

	getNameLabel(name: string | undefined) {
		return getNameLabel(name as string);
	}

	handleShowAutocomplete(event?: Event) {
		this.autocompleteOverlayRef.toggle(this.searchHasFocus, event?.target);
	}

	login() {
		this.subs.add(this.authFacade.logout().subscribe());
	}

	logout() {
		this.isLoggingOut = true;
		this.subs.add(this.authFacade.logout().subscribe());
	}

	navigateToSettings() {
		if (!this.settingsButton) return;
		this._router.navigate(this.settingsButton.routerLink);
	}

	openCart() {
		return this._router.navigate(['account', 'cart']);
	}

	cartDialog() {
		this.ref = this._dialogService.open(CartDialogComponent, {
			header: 'Carrinho',
			modal: true,
			styleClass: 'border-round overflow-hidden',
			width: '540px',
		});

		this.subs.add(this.ref.onClose.subscribe(() => {}));
	}

	favDialog() {
		this.ref = this._dialogService.open(FavoritesDialogComponent, {
			header: 'Favoritos',
			modal: true,
			styleClass: 'border-round overflow-hidden',
			width: '540px',
		});

		this.subs.add(this.ref.onClose.subscribe(() => {}));
	}

	searchProduct(search: string, hideOverlay = false) {
		this.search = search;
		if (!search) return;

		if (hideOverlay) this.autocompleteOverlayRef.hide();

		this._router.navigate(['/'], {
			queryParams: { search, page: 1, category: undefined },
			queryParamsHandling: 'merge',
		});
	}

	get showDashboard() {
		return this.tenantsFacade.hasFeatures('dashboard');
	}

	// -------------------------------------------------------------------
	// Seller order CTA helpers
	// -------------------------------------------------------------------

	private buildSellerOrderMenu() {
		this.sellerOrderMenuItems = [
			{
				label: 'Novo pedido',
				icon: 'pi pi-plus',
				command: () => this.openSellerOrder('new'),
			},
			// O card não pede repetir o último pedido enviado.
			// {
			// 	label: 'Repetir último',
			// 	icon: 'pi pi-replay',
			// 	command: () => this.openSavedDraft(),
			// },
			{
				label: `Continuar rascunho`,
				icon: 'pi pi-file-edit',
				badge: this.draftCount > 0 ? '1' : undefined,
				disabled: this.draftCount === 0,
				command: () => this.openSellerOrder('resume'),
			},
		];
	}

	private openSellerOrder(intent: 'new' | 'resume') {
		this.sellerOrderFacade.requestEntry(intent);
		if (this.isOnSellerOrderPage) return;
		this._router.navigate(['/orders/novo-pedido']);
	}

	get isOnSellerOrderPage(): boolean {
		return this._router.url.startsWith('/orders/novo-pedido');
	}

	navigateToNewOrder() {
		this.openSellerOrder('new');
	}
}
